import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, ChevronLeft, ChevronRight, Maximize2, Minimize2, X, FileText, Monitor, Mail, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const TOTAL_SLIDES = 18;

const VisitorSystemPage = () => {
  const { language: lang } = useLanguage();
  
  const [currentSlide, setCurrentSlide] = useState(1);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const pdfUrl = lang === 'de' 
    ? '/assets/visitor/besucherunterweisungssystem_de.pdf'
    : '/assets/visitor/visitor_instruction_en.pdf';
    
  const pdfTitle = lang === 'de' 
    ? 'Besucherunterweisungssystem - Produktinformation'
    : 'Visitor Instruction System - Product Information';

  const nextSlide = () => {
    setCurrentSlide(prev => prev < TOTAL_SLIDES ? prev + 1 : 1);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => prev > 1 ? prev - 1 : TOTAL_SLIDES);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const slideUrl = `/assets/visitor/slides_de/slide_${String(currentSlide).padStart(2, '0')}.jpg`;

  const seoContent = {
    de: {
      title: 'Besucherunterweisungssystem - Digitale Fremdfirmenunterweisung',
      description: 'e-Matrix Besucherunterweisungssystem: Besucher, Fremdfirmen und Kontraktoren digital unterweisen. Zutrittskontrolle, automatische Dokumentation, DSGVO-konform. Rechtssicher.',
      keywords: 'Besucherunterweisungssystem, Fremdfirmenunterweisung, Besuchermanagement, Kontraktoren Unterweisung, digitale Besucherunterweisung, Zutrittskontrolle E-Learning, Fremdfirmenmanagement, Sicherheitsunterweisung Besucher, Besucherregistrierung'
    },
    en: {
      title: 'Visitor Instruction System - Digital Contractor Training',
      description: 'e-Matrix Visitor Instruction System: Digitally instruct visitors, external companies and contractors. Access control, automatic documentation, GDPR-compliant. Legally secure.',
      keywords: 'Visitor instruction system, contractor training, visitor management, contractor instruction, digital visitor instruction, access control E-Learning, contractor management, safety instruction visitors, visitor registration'
    }
  };

  const seo = seoContent[lang] || seoContent.de;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E7F0FA] to-white pt-20">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path="/visitor-system"
        lang={lang}
      />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#C8D8E8]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link 
            to="/solutions" 
            className="inline-flex items-center gap-2 text-[#2E5E99] hover:text-[#1E4F85] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'de' ? 'Zurück zu Lösungen' : 'Back to Solutions'}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
            {lang === 'de' ? 'Besucherunterweisungssystem' : 'Visitor Instruction System'}
          </h1>
          <p className="text-[#5A7A9B] mt-2 text-lg">
            {lang === 'de' 
              ? 'Effiziente und rechtssichere Unterweisung für Besucher und Fremdfirmen'
              : 'Efficient and legally compliant instruction for visitors and external companies'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero Image */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/visitor/images/img_1_1.jpeg" 
              alt={lang === 'de' ? 'Besucherunterweisungssystem' : 'Visitor Instruction System'}
              className="w-full h-[400px] object-cover"
            />
          </div>
        </motion.section>

        {/* Key Features with Images */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-[#0D2440] mb-8 text-center">
            {lang === 'de' ? 'Drei Wege zur Unterweisung' : 'Three Ways to Instruction'}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Option 1: On-site */}
            <div className="bg-white rounded-xl shadow-lg border border-[#C8D8E8] hover:shadow-xl transition-shadow p-6">
              <div className="w-12 h-12 bg-[#2E5E99]/10 rounded-lg flex items-center justify-center mb-4">
                <Monitor className="w-6 h-6 text-[#2E5E99]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0D2440] mb-2">
                {lang === 'de' ? 'Unterweisung vor Ort' : 'On-site Instruction'}
              </h3>
              <p className="text-[#5A7A9B] text-sm">
                {lang === 'de' 
                  ? 'Besucher registrieren sich am Terminal, absolvieren die Unterweisung vor Ort und erhalten nach erfolgreichem Abschluss eine Zugangskarte.'
                  : 'Visitors register at the terminal, complete the instruction on-site and receive an access card after successful completion.'}
              </p>
            </div>
            
            {/* Option 2: Before visit */}
            <div className="bg-white rounded-xl shadow-lg border border-[#C8D8E8] hover:shadow-xl transition-shadow p-6">
              <div className="w-12 h-12 bg-[#2E5E99]/10 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-[#2E5E99]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0D2440] mb-2">
                {lang === 'de' ? 'Unterweisung vor dem Besuch' : 'Instruction Before Visit'}
              </h3>
              <p className="text-[#5A7A9B] text-sm">
                {lang === 'de' 
                  ? 'Besucherdaten werden vorab erfasst, der Besucher erhält einen Einladungslink, absolviert die Unterweisung von zu Hause und erhält einen Zugangscode.'
                  : 'Visitor data is collected in advance, the visitor receives an invitation link, completes the instruction from home and receives an access code.'}
              </p>
            </div>
            
            {/* Option 3: External company */}
            <div className="bg-white rounded-xl shadow-lg border border-[#C8D8E8] hover:shadow-xl transition-shadow p-6">
              <div className="w-12 h-12 bg-[#2E5E99]/10 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-[#2E5E99]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0D2440] mb-2">
                {lang === 'de' ? 'Fremdfirmenzugang' : 'External Company Access'}
              </h3>
              <p className="text-[#5A7A9B] text-sm">
                {lang === 'de' 
                  ? 'Fremdfirmen verwalten ihre Mitarbeiter eigenständig, weisen relevante Unterweisungen zu und kontrollieren die Einhaltung vor Betreten des Geländes.'
                  : 'External companies manage their employees independently, assign relevant instructions and verify compliance before entering the premises.'}
              </p>
            </div>
          </div>
        </motion.section>

        {/* PDF Download Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-[#0D2440] mb-6">
            {lang === 'de' ? 'Produktinformation' : 'Product Information'}
          </h2>
          
          <div className="bg-white rounded-xl p-6 border border-[#C8D8E8] shadow-lg">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-xl font-semibold text-[#0D2440]">{pdfTitle}</h3>
                <p className="text-[#5A7A9B] mt-1">
                  {lang === 'de' 
                    ? 'Detaillierte Informationen zum Besucherunterweisungssystem als PDF'
                    : 'Detailed information about the Visitor Instruction System as PDF'}
                </p>
              </div>
            </div>
            
            {/* PDF Preview */}
            <div className="bg-[#E7F0FA] rounded-lg p-8 text-center">
              <FileText className="w-16 h-16 text-[#2E5E99] mx-auto mb-4" />
              <h4 className="text-[#0D2440] font-semibold mb-2">{pdfTitle}</h4>
              <p className="text-[#5A7A9B] mb-6">
                {lang === 'de' 
                  ? 'Klicken Sie auf den Button um das PDF zu öffnen oder herunterzuladen.'
                  : 'Click the button to open or download the PDF.'}
              </p>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#2E5E99] hover:bg-[#1E4F85] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                data-testid="pdf-download-btn"
              >
                <Download className="w-5 h-5" />
                {lang === 'de' ? 'PDF öffnen' : 'Open PDF'}
              </a>
            </div>
          </div>
        </motion.section>

        {/* Interactive Walkthrough */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-[#0D2440] mb-6">
            {lang === 'de' ? 'System-Präsentation' : 'System Presentation'}
          </h2>
          
          <div className="bg-white rounded-xl p-6 border border-[#C8D8E8] shadow-lg">
            <p className="text-[#5A7A9B] mb-4">
              {lang === 'de' 
                ? 'Interaktive Präsentation des Besucherunterweisungssystems. Navigieren Sie durch die Slides mit den Pfeiltasten.'
                : 'Interactive presentation of the Visitor Instruction System. Navigate through the slides with the arrow buttons.'}
            </p>
            
            {!showSlideshow ? (
              <Button
                onClick={() => setShowSlideshow(true)}
                className="bg-[#2E5E99] hover:bg-[#1E4F85] text-white"
                data-testid="start-presentation-btn"
              >
                {lang === 'de' ? 'Präsentation starten' : 'Start Presentation'}
              </Button>
            ) : (
              <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white p-4' : ''}`}>
                {/* Slideshow Header */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[#0D2440] font-medium">
                    {lang === 'de' ? 'Interaktive Präsentation' : 'Interactive Presentation'}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleFullscreen}
                      className="p-2 bg-[#E7F0FA] hover:bg-[#D0E0F0] rounded-lg transition-colors"
                      title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                    >
                      {isFullscreen ? <Minimize2 className="w-5 h-5 text-[#0D2440]" /> : <Maximize2 className="w-5 h-5 text-[#0D2440]" />}
                    </button>
                    <button
                      onClick={() => { setShowSlideshow(false); setIsFullscreen(false); }}
                      className="p-2 bg-[#E7F0FA] hover:bg-[#D0E0F0] rounded-lg transition-colors"
                      title={lang === 'de' ? 'Schließen' : 'Close'}
                    >
                      <X className="w-5 h-5 text-[#0D2440]" />
                    </button>
                  </div>
                </div>
                
                {/* Slideshow Content */}
                <div className={`relative bg-[#E7F0FA] rounded-lg overflow-hidden ${isFullscreen ? 'h-[calc(100vh-120px)]' : 'aspect-video'}`}>
                  <img
                    src={slideUrl}
                    alt={`Slide ${currentSlide}`}
                    className="w-full h-full object-contain"
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 hover:bg-white rounded-full transition-colors shadow-lg"
                    data-testid="prev-slide-btn"
                  >
                    <ChevronLeft className="w-6 h-6 text-[#0D2440]" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 hover:bg-white rounded-full transition-colors shadow-lg"
                    data-testid="next-slide-btn"
                  >
                    <ChevronRight className="w-6 h-6 text-[#0D2440]" />
                  </button>
                </div>
                
                {/* Slide Counter */}
                <div className="flex items-center justify-center mt-4 gap-4">
                  <span className="text-[#0D2440] font-medium">
                    {currentSlide} / {TOTAL_SLIDES}
                  </span>
                </div>
                
                {/* Thumbnail Navigation */}
                <div className="flex items-center justify-center gap-1 mt-4 overflow-x-auto pb-2">
                  {Array.from({ length: TOTAL_SLIDES }, (_, i) => i + 1).map(num => (
                    <button
                      key={num}
                      onClick={() => setCurrentSlide(num)}
                      className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium transition-colors ${
                        currentSlide === num 
                          ? 'bg-[#2E5E99] text-white' 
                          : 'bg-[#E7F0FA] text-[#5A7A9B] hover:bg-[#D0E0F0]'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="bg-gradient-to-r from-[#2E5E99] to-[#1E4F85] rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {lang === 'de' ? 'Interesse an unserem Besucherunterweisungssystem?' : 'Interested in our Visitor Instruction System?'}
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              {lang === 'de' 
                ? 'Kontaktieren Sie uns für eine individuelle Beratung und ein unverbindliches Angebot.'
                : 'Contact us for individual consultation and a non-binding offer.'}
            </p>
            <Link
              to="/contact?type=visitor"
              className="inline-block bg-white text-[#2E5E99] hover:bg-white/90 px-8 py-3 rounded-lg font-medium transition-colors"
            >
              {lang === 'de' ? 'Kontakt aufnehmen' : 'Contact Us'}
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default VisitorSystemPage;
